/**
 * F 217 — neighbour-aware block spacing. Option A, Notion-faithful.
 *
 * SCOPE, AND WHY IT IS THIS NARROW.
 *
 * Notion's own writeup ("Updating the design of Notion pages") describes
 * adjacency for LISTS and lists only:
 *
 *   "We apply this logic to anything we deem to be in a 'list,' which
 *    includes checklists, bulleted lists, toggle lists, numbered lists,
 *    and lists of pages. When a list item's neighbor is another list
 *    item, we reduce the padding to make them chunk together neatly."
 *
 * That is the whole rule. There is NO heading-vs-paragraph rule in the
 * source. F 216's earlier notes claimed one; that claim was a guess
 * carried forward as a citation, and the operator picked Option A to
 * stop doing that. So this module has exactly one rule, and the
 * non-vacuity test in `__tests__/blockSpacing.spec.ts` asserts the
 * ABSENCE of a heading rule so a later agent cannot quietly add one.
 *
 * If a second rule is ever wanted, it needs a source. Add it here, with
 * the citation, and add its test. Do not add it because it looks right.
 */

/** The current inter-block baseline, measured live in F 216.
 *  Every `.bn-block-content` in FCC today is a flat 3px/3px. That value
 *  is BlockNote's default, not an FCC spacing token, which is why
 *  DEFAULT_TOP_MARGIN is 3 and not a step on the 4/8/12/16/24/32/48
 *  scale. The reduced value, by contrast, IS on that scale. */
export const DEFAULT_TOP_MARGIN = 3;

/** Notion's reduction. On F 211's scale (--fc-space-0). Chosen as 0
 *  rather than 2 so a list item following a list item sits flush, and
 *  so the number is unambiguously "the mechanism fired" rather than
 *  "some padding". */
export const REDUCED_TOP_MARGIN = 0;

/**
 * BlockNote 0.47's default list block types. Read out of the shipped
 * schema, not assumed: the node names are checkListItem, toggleListItem,
 * bulletListItem, numberedListItem. Note this is NOT "listItem" — an
 * earlier F 216 note called list items "listItem", which matches no
 * node in this schema and would have made the rule a silent no-op.
 */
export const LIST_KINDS: ReadonlySet<string> = new Set([
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'toggleListItem',
]);

export function isListKind(kind: string | null | undefined): boolean {
  return !!kind && LIST_KINDS.has(kind);
}

export interface BlockSpacing {
  /** px the block's top margin should contribute. */
  topMargin: number;
}

/** Memo. The key space is (kinds x kinds) and the kinds come from a
 *  fixed schema, so this saturates immediately and never grows. Kept
 *  because Stage 3 asked for it, not because it is needed. */
const cache = new Map<string, BlockSpacing>();

/**
 * The one rule.
 *
 * listItem -> listItem  : reduced  (Notion's rule)
 * everything else      : default  (no special adjacency)
 *
 * A null prevKind is the first block in the document. There is no
 * neighbour, so there is no adjacency, so it gets the default.
 *
 * @param prevKind    BlockNote block type of the preceding sibling, or
 *                    null when this is the first block.
 * @param currentKind BlockNote block type of this block.
 */
export function deriveSpacing(
  prevKind: string | null | undefined,
  currentKind: string | null | undefined,
): BlockSpacing {
  const key = `${prevKind ?? ''} ${currentKind ?? ''}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const topMargin =
    isListKind(prevKind) && isListKind(currentKind)
      ? REDUCED_TOP_MARGIN
      : DEFAULT_TOP_MARGIN;

  const out: BlockSpacing = { topMargin };
  cache.set(key, out);
  return out;
}
